import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const activeSlug = slug || 'all';
  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const filteredMovies = useMemo(() => {
    let result: Movie[] = activeSlug === 'all' ? [...movies] : movies.filter((m) => m.categorySlug === activeSlug);

    if (query) {
      const lower = query.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(lower) ||
          m.tags.some((t) => t.toLowerCase().includes(lower)) ||
          m.category.toLowerCase().includes(lower)
      );
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result.sort((a, b) => b.views - a.views);
        break;
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }

    return result;
  }, [activeSlug, sortBy, query]);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '影视分类', to: '/category' },
  ];

  if (activeSlug !== 'all' && activeCategory) {
    breadcrumbItems.push({ label: activeCategory.name });
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug, sortBy]);

  const pageTitle = activeCategory
    ? `${activeCategory.name} - 天美麻花星空影视免费观看高清不缓冲`
    : '麻花星空影视免费观看高清不缓冲流畅播放 | 极速观看免费影视大全';
  const pageDesc = activeCategory
    ? `天美麻花星空影视${activeCategory.name}频道，提供${activeCategory.name}免费在线观看，高清不卡顿流畅播放。`
    : '天美麻花星空影视免费观看，麻花星空影视高清免费在线播放，不缓冲不卡顿，极速流畅观看电影电视剧综艺，免费影视大全。';

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{pageTitle}</h1>
                <p className="text-foreground-500 text-sm">{pageDesc}</p>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-8">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.slug === 'all' ? '/category' : `/category/${cat.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      window.REACT_APP_NAVIGATE(cat.slug === 'all' ? '/category' : `/category/${cat.slug}`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-white/5 text-foreground-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>

              <div className="flex items-center justify-between mb-6">
                <p className="text-foreground-500 text-sm">
                  共找到 <strong className="text-white">{filteredMovies.length}</strong> 部影片
                </p>
                <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                  {[
                    { key: 'latest' as const, label: '最新' },
                    { key: 'rating' as const, label: '评分' },
                    { key: 'views' as const, label: '热度' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-primary-500 text-background-950'
                          : 'text-foreground-500 hover:text-white'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length === 0 ? (
                <div className="py-20 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-white/5">
                    <i className="ri-search-line text-2xl text-foreground-600"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关影片</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-12">
                  {filteredMovies.map((movie) => (
                    <div key={movie.id} className="flex justify-center">
                      <MovieCard movie={movie} size="md" showDescription />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}